/**
 * product-essentials-alma.js
 * Widget Alma de la buybox (fiche produit) : ligne d'échéancier au survol + clic
 * détourné vers la modale de financement du thème.
 *
 * Le widget lui-même reste l'OFFICIEL (app block Alma, cf. snippets/product-essentials-alma.liquid).
 * Ce script ne fait que deux choses par-dessus :
 *
 *   1. CLIC — un clic sur une échéance (2x, 3x, 4x, 10x) ouvre la modale de
 *      financement du thème sur CE plan (GreenleazeLocationModal, configurateur Alma)
 *      au lieu de la modale d'éligibilité d'Alma.
 *   2. SURVOL — la ligne sous le widget annonce le plan pointé. Elle RETIENT la dernière
 *      échéance pointée : sortir du widget ne la remet pas au défaut, le widget d'Alma
 *      gardant lui aussi sa puce sélectionnée. Avant toute interaction, elle SUIT le
 *      défilement automatique d'Alma (qui met ses plans en avant à tour de rôle) ; le
 *      plan par défaut, 2×, ne sert que tant que ce défilement n'est pas lisible.
 *   3. MESURE — `view_cta` à l'impression (widget rendu + 50 % visible, une fois par
 *      page) et `click_cta` au clic, avec les MÊMES dimensions : le taux de clic est
 *      le rapport direct des deux.
 *
 * --- Montants -------------------------------------------------------------
 * Tous les montants viennent de `GreenleazeFinancing.alma()` : ce sont ceux d'Alma,
 * lus dans sa modale d'éligibilité (ouverte masquée, une seule fois par page, cache
 * partagé avec la modale de location). On ne calcule RIEN localement — un « prix ÷ n »
 * serait faux dès qu'un plan porte des frais, et « sans frais » ne peut pas être
 * présumé : il est affiché seulement quand Alma annonce un coût du crédit NUL
 * (`cost === 0`). Coût illisible ⇒ on n'affirme rien, on se contente du montant.
 *
 * La lecture est déclenchée à l'inactivité APRÈS le `load` : elle ouvre une modale
 * React masquée (~100 ms de thread principal), qui n'a rien à faire dans le chemin
 * critique du premier rendu. La ligne réserve sa hauteur en CSS, donc son arrivée
 * ~1 s plus tard ne décale rien.
 *
 * --- Dégradations assumées ------------------------------------------------
 * Chaque étage tombe indépendamment, et l'absence de l'un ne casse jamais l'autre :
 *   - pas de `GreenleazeFinancing` (produit hors location : le script est chargé par
 *     snippets/greenleaze-location-modal.liquid, lui-même conditionné au tag
 *     « location ») ⇒ pas de ligne, et AUCUNE interception : le widget se comporte
 *     exactement comme l'officiel, modale Alma comprise ;
 *   - modale du thème non rendue ou désactivée pour l'appareil
 *     (`isEnabledForDevice`) ⇒ idem, on laisse le clic à Alma ;
 *   - Alma sans offre lisible ⇒ la ligne reste vide, le clic reste à Alma.
 * On ne détourne donc jamais un clic sans avoir de quoi l'honorer.
 *
 * --- Habillage --------------------------------------------------------------
 * Le logo affiché est LE NÔTRE (pastille orange rendue par le Liquid, hors du widget) ;
 * celui d'Alma est masqué, et son wordmark officiel cloné dedans. C'est un choix, pris
 * après trois tentatives infructueuses de styler le logo d'Alma en place : le centrage
 * vertical et le bord gauche du bloc dépendaient de SA mise en page — quel nœud est
 * l'élément flex, qui porte le retrait à gauche —, invérifiable d'ici et non figée dans
 * le temps. Avec notre propre pastille, les deux redeviennent de simples règles CSS à
 * nous. Voir le commentaire de paintLogo pour le détail.
 *
 * --- Accroches DOM d'Alma -------------------------------------------------
 * ⚠️ TOUTE la connaissance du markup d'Alma vit ICI (avec greenleaze-financing-data.js) :
 * rien dans le CSS. Le bundle flotte en `@alma/widgets@3.x.x` — v3 rend les échéances
 * en <div> survolés, v4 en <button>, et les noms de classe changent de majeure en
 * majeure. On ne cible donc NI un tag NI une classe pour trouver une échéance : on
 * remonte depuis la cible de l'événement le premier ancêtre dont le texte entier est
 * exactement « N x » / « N× ». C'est ce que le widget affiche, dans les deux majeures.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-alma-widget]';

  // Ligne d'info native d'Alma : on la masque, notre ligne la remplace (même
  // information, format de la maquette). Masquée, PAS retirée : `almaInline()` de
  // greenleaze-financing-data.js lit son textContent comme repli quand la lecture de
  // la modale d'éligibilité échoue.
  var ALMA_INFO_SELECTOR = '.alma-payment-plans-payment-info, #payment-info-text';

  // Plan mis en avant au repos (décision produit, 2026-08-05 : 2×). S'il n'est pas
  // proposé pour ce prix, on prend le plan le plus proche — jamais rien, la ligne ne
  // doit pas rester vide alors que des offres existent.
  var DEFAULT_INSTALLMENTS = 2;

  var euro = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });

  function financingApi() {
    return window.GreenleazeFinancing || null;
  }

  // Émission d'un event du bloc sticky depuis CE fichier. Nécessaire pour deux raisons :
  //  - `click_cta` : stopper la propagation pour neutraliser Alma prive le délégué de
  //    tracking (posé sur document en remontée, snippets/tracking-ecommerce.liquid) de
  //    l'événement ;
  //  - `view_cta` : une impression n'est pas un clic, aucun délégué ne peut la voir.
  // Le payload vient du constructeur exposé par le snippet de tracking,
  // `window.mbStickyCtaPayload` — pas d'un second format maison. Le repli minimal ne
  // sert qu'au cas où ce snippet ne serait pas rendu.
  function pushCta(eventName, ctaName, installments) {
    var payload =
      typeof window.mbStickyCtaPayload === 'function'
        ? window.mbStickyCtaPayload(ctaName, 'product_sticky')
        : { cta_name: ctaName, cta_location: 'product_sticky' };
    if (installments != null) payload.installments = installments;

    if (typeof window.trackEvent === 'function') {
      window.trackEvent(eventName, payload);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    payload.event = eventName;
    window.dataLayer.push(payload);
  }

  // La modale du thème peut-elle réellement s'ouvrir ? Testé AVANT d'intercepter quoi
  // que ce soit : décider après aurait déjà avalé le clic destiné à Alma.
  function modalAvailable() {
    var api = window.GreenleazeLocationModal;
    return !!(api && typeof api.isEnabledForDevice === 'function' && api.isEnabledForDevice());
  }

  // --- Lecture d'une échéance dans le DOM d'Alma ----------------------------
  // On remonte depuis `target` jusqu'à `root` et on retient le premier nœud dont le
  // TEXTE ENTIER est un libellé d'échéance. Le texte entier, et pas « contient » : la
  // ligne d'info (« 808,16 € puis 3 x 794,50 € ») contient elle aussi « 3 x ».
  function planFrom(target, root) {
    var el = target;
    while (el && el !== root && el.nodeType === 1) {
      // `\s` couvre les insécables (U+00A0 / U+202F) qu'Alma emploie dans ses libellés.
      var text = (el.textContent || '').replace(/\s/g, '').toLowerCase();
      var match = text.match(/^(\d+)[x×]$/);
      if (match) {
        var count = parseInt(match[1], 10);
        if (count > 1) return count;
      }
      el = el.parentElement;
    }
    return null;
  }

  // --- Formatage de la ligne ------------------------------------------------
  // « 4× de 570 € sans frais » / « 10× de 245 € — TAEG 19,9 % ».
  // `monthly` est la 2e échéance (cf. readActivePlan) : sur un plan à frais, la 1re les
  // porte et n'est pas représentative. Repli sur la 1re quand c'est tout ce qu'on a.
  function lineFor(offer) {
    if (!offer || !offer.installments) return '';
    var amount = offer.monthly != null ? offer.monthly : offer.firstAmount;
    if (amount == null) return '';

    var text = offer.installments + '× ' + euro.format(Math.round(amount)) + ' €';

    if (offer.cost === 0) return text + ' sans frais';
    // Offre payante : on signale les frais, le TAEG est détaillé dans la modale de financement.
    if (offer.cost > 0) return text + ' avec frais';
    // Coût du crédit illisible : on affiche le montant sans qualifier les frais.
    return text;
  }

  function pickDefault(offers) {
    if (!offers.length) return null;
    return offers.reduce(function (best, offer) {
      if (!best) return offer;
      var d1 = Math.abs(offer.installments - DEFAULT_INSTALLMENTS);
      var d2 = Math.abs(best.installments - DEFAULT_INSTALLMENTS);
      // Égalité de distance (2× et 4× face à un défaut de 3×) : on prend le plan le
      // plus court, donc le moins engageant.
      if (d1 < d2) return offer;
      if (d1 === d2 && offer.installments < best.installments) return offer;
      return best;
    }, null);
  }

  // --- Une instance de widget ----------------------------------------------
  function setup(root) {
    if (root.__mbAlmaWired) return;
    root.__mbAlmaWired = true;

    var widgetEl = root.querySelector('.product-essentials-block__alma-widget');
    var logoEl = root.querySelector('[data-alma-logo]');
    var lineEl = root.querySelector('[data-alma-line]');
    var textEl = root.querySelector('[data-alma-line-text]');
    var offers = [];
    var defaultOffer = null;

    // Échéance retenue — `null` tant que rien n'a été pointé, et le plan par défaut fait
    // alors foi. PERSISTANTE : quitter le widget ne la rend PAS au défaut.
    // Pourquoi : le widget d'Alma garde sa puce sélectionnée sur la dernière échéance
    // pointée. Une ligne qui repasserait au défaut en sortant contredirait la puce encore
    // marquée juste au-dessus — et le clic sur la ligne n'ouvrirait pas le plan qu'on
    // venait de lire. La ligne DIT ce que la puce MONTRE.
    var currentPlan = null;

    // Échéance que le widget met en avant TOUT SEUL. Alma fait défiler ses plans tant
    // que l'utilisateur n'a rien pointé : la puce en surbrillance change d'elle-même, et
    // une ligne figée sur le plan par défaut annonçait alors une autre échéance que
    // celle affichée juste au-dessus. On suit donc le défilement.
    // ⚠️ `currentPlan` PRIME : dès que l'utilisateur pointe une échéance, Alma arrête son
    // défilement et son choix ne doit plus bouger (cf. syncAutoPlan).
    var autoPlan = null;

    function render() {
      if (!textEl) return;
      var offer = null;
      // Choix de l'utilisateur > échéance mise en avant par le défilement > plan par défaut.
      var plan = currentPlan != null ? currentPlan : autoPlan;
      if (plan != null) {
        offer = offers.filter(function (o) {
          return o.installments === plan;
        })[0];
      }
      // Échéance inconnue d'Alma (pointée avant la lecture des offres, ou disparue au
      // changement de variante) : le plan par défaut reprend la main, sans effacer le
      // choix — il redeviendra affichable si l'offre réapparaît.
      if (!offer) offer = defaultOffer;

      textEl.textContent = lineFor(offer);
      // Plan courant exposé au délégué de tracking (tracking-ecommerce.liquid), qui
      // enrichit le click_cta sans avoir à reparser le DOM d'Alma.
      if (offer) root.setAttribute('data-alma-plan', String(offer.installments));
      else root.removeAttribute('data-alma-plan');
      if (lineEl) lineEl.toggleAttribute('data-alma-line-ready', !!offer);
    }

    function select(installments) {
      if (installments === currentPlan) return;
      currentPlan = installments;
      render();
    }

    // 🔴 `alma()` ne mémorise RIEN tant que le widget n'est pas dans le DOM (l'app Alma
    // est chargée en async par son app embed) : elle renvoie alors une réponse jetable.
    // Sans relance, une lecture partie trop tôt condamnerait la ligne pour toute la page
    // — c'est exactement le piège que watchOtherSolutions désamorce côté modale. On
    // repasse donc jusqu'à obtenir des offres. Les tentatives qui suivent une lecture
    // RÉELLE sont gratuites : `alma()` resert sa promesse mémorisée sans rouvrir la
    // modale d'éligibilité.
    var LOAD_TRIES = 8;
    var LOAD_INTERVAL_MS = 600; // ~5 s au total, le temps qu'un widget tiers rende
    var loadTimer = null;

    function load(attempt) {
      window.clearTimeout(loadTimer);
      var api = financingApi();
      if (!api || typeof api.alma !== 'function') return;
      var tries = typeof attempt === 'number' ? attempt : 0;

      api.alma().then(function (res) {
        offers = (res && res.available && res.offers ? res.offers : []).filter(function (o) {
          return o && o.installments > 1;
        });
        defaultOffer = pickDefault(offers);
        // `render` et non `select(null)` : une échéance déjà pointée pendant que les
        // offres se chargeaient doit être honorée, pas écrasée par le plan par défaut.
        render();

        if (offers.length || tries + 1 >= LOAD_TRIES) return;
        loadTimer = window.setTimeout(function () {
          load(tries + 1);
        }, LOAD_INTERVAL_MS);
      });
    }

    // Ligne native d'Alma masquée dès qu'elle apparaît : le widget rend en asynchrone,
    // elle n'est pas forcément là au moment où ce script s'exécute.
    // ⚠️ Écriture GARDÉE : l'observateur écoute les attributs, et réécrire `style` avec
    // la même valeur émet quand même une mutation — donc une boucle sans fin.
    function hideNativeLine() {
      var native = root.querySelector(ALMA_INFO_SELECTOR);
      if (!native) return false;
      if (native.style.display !== 'none') native.style.display = 'none';
      return true;
    }

    // --- Suivi du défilement automatique d'Alma ------------------------------
    // Quelle échéance le widget met-il en avant à cet instant ? On le lit sur les PUCES
    // elles-mêmes, à leur apparence calculée — c'est la vérité visuelle, celle que la
    // ligne doit refléter.
    //
    // Pourquoi pas la ligne d'info d'Alma (première tentative) : elle dépend d'un format
    // de phrase (« X € puis N x Y € ») qui n'est pas garanti sur tous les plans.
    // Pourquoi pas une classe « active » : Alma la renomme d'une majeure à l'autre.
    // L'apparence, elle, est observable sans rien présumer : la puce mise en avant est
    // celle dont la signature visuelle diffère de ses voisines. On ne présume même pas de
    // LA propriété employée (fond, couleur de texte, graisse, bordure) — on les
    // concatène et on cherche l'intrus.
    //
    // Ambigu (aucun intrus, ou plusieurs — typiquement pendant une transition CSS) ⇒ on
    // ne conclut pas et la valeur précédente reste. Jamais de ligne fausse.

    // Une entrée par échéance : la PLUS GRANDE boîte dont le texte entier est « N× »,
    // c'est-à-dire la puce stylée et non le <span> de texte qu'elle enveloppe.
    function chipMap() {
      var nodes = widgetEl.querySelectorAll('*');
      var byPlan = {};
      for (var i = 0; i < nodes.length; i++) {
        var text = (nodes[i].textContent || '').replace(/\s/g, '').toLowerCase();
        var match = text.match(/^(\d+)[x×]$/);
        if (!match) continue;
        var plan = parseInt(match[1], 10);
        if (!(plan > 1)) continue;

        var rect = nodes[i].getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) continue;
        var area = rect.width * rect.height;
        if (!byPlan[plan] || area > byPlan[plan].area) byPlan[plan] = { el: nodes[i], area: area };
      }
      return byPlan;
    }

    function chipSignature(el) {
      var style = window.getComputedStyle(el);
      return [style.backgroundColor, style.color, style.fontWeight, style.borderTopColor, style.opacity].join('|');
    }

    function highlightedPlan() {
      var byPlan = chipMap();
      var plans = Object.keys(byPlan);
      if (plans.length < 2) return null;

      var counts = {};
      var signatures = {};
      plans.forEach(function (plan) {
        var signature = chipSignature(byPlan[plan].el);
        signatures[plan] = signature;
        counts[signature] = (counts[signature] || 0) + 1;
      });

      var odd = null;
      for (var i = 0; i < plans.length; i++) {
        if (counts[signatures[plans[i]]] !== 1) continue;
        if (odd !== null) return null; // plusieurs intrus : on ne tranche pas
        odd = parseInt(plans[i], 10);
      }
      return odd;
    }

    function syncAutoPlan() {
      if (currentPlan != null) return; // l'utilisateur a tranché : le défilement ne reprend pas la main
      var plan = highlightedPlan();
      if (plan == null || plan === autoPlan) return; // garde-fou anti-boucle : pas de rendu inutile
      autoPlan = plan;
      render();
    }

    // Les mutations arrivent en rafale (Alma bascule plusieurs attributs par pas de
    // défilement) : on ne relit les styles calculés qu'une fois par frame.
    var autoPlanFrame = null;

    function scheduleAutoPlan() {
      if (autoPlanFrame || typeof window.requestAnimationFrame !== 'function') {
        if (!autoPlanFrame) syncAutoPlan();
        return;
      }
      autoPlanFrame = window.requestAnimationFrame(function () {
        autoPlanFrame = null;
        syncAutoPlan();
      });
    }

    // --- Logo Alma : LE NÔTRE, pas le sien -----------------------------------
    // Styler le logo d'Alma en place a échoué trois fois, sur des inconnues de SA mise
    // en page qu'on ne peut ni observer d'ici ni figer dans le temps : quel nœud est
    // l'élément flex de la rangée ? qui porte le retrait à gauche — la racine d'Alma, le
    // wrapper `.shopify-block` de Shopify, le logo lui-même ? Aucune réponse ne tient,
    // son bundle flottant en `@alma/widgets@3.x.x`.
    //
    // On inverse donc la charge : NOTRE pastille, rendue par le Liquid HORS du widget,
    // et le logo d'Alma masqué. Tout ce qui posait problème redevient déterministe :
    //   - centrage vertical → `align-items: center` sur NOTRE rangée ;
    //   - bord gauche → notre pastille EST le premier élément de la rangée, le retrait
    //     interne d'Alma ne décale donc plus le bloc (il n'élargit plus que l'espace
    //     entre la pastille et la 1re puce). alignLeftEdge n'a plus lieu d'être ;
    //   - taille, rayon, couleurs → notre CSS.
    // Ne reste chez Alma qu'UN seul geste : masquer son logo.
    //
    // Le wordmark OFFICIEL est cloné depuis le widget — même mécanisme que la modale de
    // financement (GreenleazeFinancing.logo). Tant que le clone n'est pas disponible, le
    // repli typographique rendu par le Liquid tient le rôle, comme dans la modale.

    function almaLogoNode() {
      // Même chaîne de repli que GreenleazeFinancing.logo() : testid en v4, puis le SVG
      // décoratif, puis le premier venu — le logo est le nœud le plus à gauche.
      return (
        widgetEl.querySelector('svg[data-testid="Alma-Logo"]') ||
        widgetEl.querySelector('svg[aria-hidden="true"]') ||
        widgetEl.querySelector('svg')
      );
    }

    function paintLogo() {
      if (!widgetEl || !logoEl) return false;
      var svg = almaLogoNode();
      if (!svg) return false;

      if (!logoEl.classList.contains('has-logo')) {
        // 🔴 CLONER AVANT DE MASQUER : cloneNode copie les styles inline. Un clone pris
        // après coup hériterait du `display: none` posé plus bas et serait invisible.
        var clone = svg.cloneNode(true);
        // Dimensions laissées au CSS. On ne retire les attributs que s'il reste un
        // viewBox pour porter le rapport d'aspect — sinon le SVG s'effondrerait.
        if (clone.getAttribute('viewBox')) {
          clone.removeAttribute('width');
          clone.removeAttribute('height');
        }
        clone.style.removeProperty('display');
        // Le nom accessible est porté par le texte de repli, qui reste dans le DOM.
        clone.setAttribute('aria-hidden', 'true');
        logoEl.appendChild(clone);
        logoEl.classList.add('has-logo');
      }

      // 🔴 Masquage par une CLASSE, jamais par un style inline.
      // `GreenleazeFinancing.logo()` CLONE ce même nœud pour la carte Alma de la modale
      // de financement, et cloneNode copie les styles inline : un `display: none` inline
      // suivait le clone et vidait la pastille de la modale (constaté le 2026-08-05).
      // La classe suit elle aussi le clone, mais la règle qui la rend effective est
      // SCOPÉE à notre conteneur — hors de la buybox, le clone s'affiche normalement.
      // ⚠️ Écriture GARDÉE, même raison que hideNativeLine : `classList.add` réécrit
      // l'attribut `class` même quand le jeton y est déjà, ce qui relancerait
      // l'observateur en boucle.
      if (!svg.classList.contains('product-essentials-block__alma-logo-source')) {
        svg.classList.add('product-essentials-block__alma-logo-source');
      }
      return true;
    }

    // --- Impression (dénominateur du taux de clic) ---------------------------
    // `view_cta` porte EXACTEMENT les mêmes dimensions que le `click_cta` du widget :
    // le taux de clic est le rapport direct des deux, sans jointure ni recoupement.
    // DEUX conditions, toutes deux nécessaires :
    //   - le widget a RÉELLEMENT rendu. Alma n'affiche rien hors éligibilité (au-delà de
    //     son plafond, notamment) : compter l'impression d'une boîte vide diluerait le
    //     taux de clic sur des fiches où cliquer était impossible.
    //   - il a été visible à au moins 50 % — seuil d'usage pour une impression, et le
    //     widget mesure ~40 px de haut, donc le seuil est atteignable partout.
    // Une seule fois par page. `installments` n'est PAS transmis : à l'impression, les
    // 4 échéances sont visibles ensemble, en attribuer une seule serait faux.
    var IMPRESSION_RATIO = 0.5;
    var impressionSent = false;
    var everVisible = false;
    var viewObserver = null;

    // Widget rendu : Alma a posé au moins un nœud à lui. Le préfixe `alma-payment-plans`
    // est le seul crochet stable d'une majeure à l'autre (cf. en-tête).
    function almaRendered() {
      return !!root.querySelector('[class*="alma-payment-plans"]');
    }

    function maybeSendImpression() {
      if (impressionSent || !everVisible || !almaRendered()) return;
      impressionSent = true;
      if (viewObserver) {
        viewObserver.disconnect();
        viewObserver = null;
      }
      pushCta('view_cta', 'alma_widget');
    }

    if (typeof IntersectionObserver === 'function') {
      viewObserver = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            everVisible = true;
            maybeSendImpression();
          }
        },
        { threshold: IMPRESSION_RATIO },
      );
      viewObserver.observe(root);
    } else {
      // Sans IntersectionObserver, on compte l'impression dès que le widget est rendu.
      // Ne rien compter serait pire : les clics de ces navigateurs, eux, seraient bien
      // remontés, et le taux dépasserait 100 %.
      everVisible = true;
    }

    // Le widget rend en asynchrone : on surveille son arrivée pour masquer sa ligne
    // native et son logo, et pour réévaluer l'impression — qui exige justement un
    // widget rendu.
    // ⚠️ L'observateur n'écoute que `childList` : les styles inline posés ici ne le
    // réveillent donc pas (pas de boucle).
    function syncWidgetDom() {
      hideNativeLine();
      paintLogo();
      scheduleAutoPlan();
      maybeSendImpression();
    }

    syncWidgetDom();

    if (typeof MutationObserver === 'function' && widgetEl) {
      // PERMANENT — plus de fenêtre de 10 s. Le défilement automatique d'Alma dure aussi
      // longtemps que l'utilisateur ne pointe rien, et React re-rend le widget à chaque
      // changement de variante (son logo et sa ligne native réapparaîtraient). Un
      // observateur posé sur un sous-arbre qui ne bouge pas ne coûte rien : la fenêtre
      // bornée ne protégeait de rien et laissait retomber les deux cas ci-dessus.
      //
      // Scopé au WIDGET, pas au bloc : nos propres écritures — la ligne d'échéancier, la
      // pastille — vivent hors de ce sous-arbre. Aucune ne peut donc réveiller
      // l'observateur, ce qui ferme la porte à une boucle render → mutation → render.
      // (`syncAutoPlan` a de toute façon son propre garde-fou.)
      //
      // 🔴 `attributes` est INDISPENSABLE, et son absence était le bug : un pas de
      // défilement bascule une CLASSE sur les puces, pas leur contenu. Sans cette option
      // on n'attrapait que les pas coïncidant par chance avec une réécriture de texte —
      // « le passage de 2 à 3 marche, mais pas 3→4 ni 4→10 » (constaté le 2026-08-05).
      // Contrepartie : nos propres écritures dans ce sous-arbre (masquage de la ligne
      // native, classe sur le logo) sont elles aussi des mutations d'attribut. D'où les
      // gardes d'idempotence dans hideNativeLine et paintLogo — écrire une valeur déjà
      // en place émet quand même un enregistrement de mutation, et donc une boucle.
      new MutationObserver(syncWidgetDom).observe(widgetEl, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
      });
    }

    // --- Survol / focus : la ligne suit l'échéance pointée, et la RETIENT ---
    // Aucun écouteur de sortie (`pointerleave`, `pointerout`) : seule une NOUVELLE
    // échéance pointée change la ligne. Quitter le widget, ou glisser sur une zone
    // neutre (le logo, un interstice), ne la remet pas au défaut — cf. `currentPlan`.
    root.addEventListener('pointerover', function (e) {
      var plan = planFrom(e.target, root);
      if (plan != null) select(plan);
    });
    root.addEventListener('focusin', function (e) {
      var plan = planFrom(e.target, root);
      if (plan != null) select(plan);
    });

    // --- Clic : la modale du thème au lieu de celle d'Alma ---
    // Phase de CAPTURE : les gestionnaires de React sont délégués à sa racine, donc en
    // remontée. On doit donc décider AVANT eux.
    root.addEventListener(
      'click',
      function (e) {
        // Clic SIMULÉ : greenleaze-financing-data.js clique le widget pour lire la
        // modale d'éligibilité d'Alma (c'est notre propre source de montants). L'avaler
        // couperait la branche sur laquelle on est assis.
        if (!e.isTrusted) return;

        var plan = planFrom(e.target, root);
        if (plan == null) return;
        // Rien à offrir en remplacement : on laisse Alma ouvrir sa modale.
        if (!modalAvailable()) return;

        e.preventDefault();
        e.stopPropagation();

        // La ligne retient l'échéance cliquée. Nécessaire au TACTILE, où aucun survol ne
        // précède le tap : sans ça, revenir de la modale laisserait la ligne sur un autre
        // plan que celui qu'on vient d'ouvrir.
        select(plan);

        // Émis ici, et une seule fois : la propagation est stoppée, donc le délégué de
        // tracking ne verra jamais ce clic — quelle que soit la suite. Le clic rejoué
        // ci-dessous ne le réveillerait pas non plus (il est simulé, et le délégué
        // ignore les clics non fiables sur ce CTA).
        pushCta('click_cta', 'alma_widget', plan);

        var opened = window.GreenleazeLocationModal.open({
          duration: 36,
          variantId: root.getAttribute('data-variant-id') || undefined,
          trigger: e.target.closest('button') || root,
          financing: { provider: 'alma', installments: plan },
        });

        // `open()` a renvoyé faux (aucun prix exploitable) : on rend son clic à Alma,
        // qui ouvrira sa propre modale. Le clic rejoué est simulé, donc filtré par la
        // garde `isTrusted` en tête de ce handler : pas de boucle.
        if (!opened && e.target.click) e.target.click();
      },
      true,
    );

    // Ligne cliquable (soulignée) : même destination que l'échéance affichée.
    if (lineEl) {
      lineEl.addEventListener('click', function (e) {
        if (!modalAvailable() || !root.hasAttribute('data-alma-plan')) return;
        e.preventDefault();
        window.GreenleazeLocationModal.open({
          duration: 36,
          variantId: root.getAttribute('data-variant-id') || undefined,
          trigger: lineEl,
          financing: {
            provider: 'alma',
            installments: parseInt(root.getAttribute('data-alma-plan'), 10),
          },
        });
      });
    }

    // Changement de variante : les montants d'Alma ne veulent plus rien dire. La purge
    // du cache est faite par le gestionnaire de greenleaze-location-modal.js, sur le
    // même événement — on relit donc en fin de tâche pour passer APRÈS lui, sinon on
    // reservirait la lecture périmée qu'il s'apprête à jeter.
    // Changement de variante : les MONTANTS d'Alma ne veulent plus rien dire, mais
    // l'échéance retenue si — c'est une préférence de l'utilisateur, pas un montant.
    // `currentPlan` survit donc ; seules les offres sont vidées puis relues, et la ligne
    // se repeuple sur le même plan. S'il n'existe pas dans les nouvelles offres, `render`
    // retombe sur le plan par défaut sans effacer le choix.
    document.addEventListener('variant:change', function () {
      offers = [];
      defaultOffer = null;
      render();
      // Rien à relancer : l'observateur du widget est permanent, il remasquera le logo et
      // la ligne native d'Alma dès que React les aura re-rendus.
      window.setTimeout(function () {
        load(0);
      }, 0);
    });

    // Lecture initiale hors du chemin critique (elle monte une modale React masquée).
    // Les deux ordonnanceurs passent un argument à leur rappel (`IdleDeadline` pour
    // requestIdleCallback) : on les enveloppe, sinon il serait pris pour le compteur
    // de tentatives et neutraliserait les relances.
    var start = function () {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(
          function () {
            load(0);
          },
          { timeout: 3000 },
        );
      } else {
        window.setTimeout(function () {
          load(0);
        }, 1200);
      }
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll(ROOT_SELECTOR), setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
